import { useCallback, useEffect } from "react";
import { getHyphenFromCamelCase, useLocation } from "@rotorstudios/fe-modules";

import useGA from "./useGA";
import { GaEvent } from "./static";
import {
  PAGE_ADVANTAGES,
  PAGE_CHARGING,
  PAGE_CUSTOMISE,
  PAGE_ERROR,
  PAGE_FEATURES,
  PAGE_FINANCE,
  PAGE_HOME,
  PAGE_NEXT_STEPS,
  PAGE_SPECIFICATIONS,
  PAGE_VEHICLE_SELECTION,
} from "../Router/static";
import { useDataContext } from "../../Data/DataProvider";

const pageTitleMap = new Map([
  [getHyphenFromCamelCase(PAGE_HOME), "Toyota For You Home Page"],
  [getHyphenFromCamelCase(PAGE_ERROR), "Toyota For You Error"],
  [getHyphenFromCamelCase(PAGE_SPECIFICATIONS), "Toyota For You Vehicle Specifications"],
  [getHyphenFromCamelCase(PAGE_NEXT_STEPS), "Toyota For You Next Steps"],
  [getHyphenFromCamelCase(PAGE_ADVANTAGES), "Toyota For You Advantages"],
  [getHyphenFromCamelCase(PAGE_CHARGING), "Charging"],
  [getHyphenFromCamelCase(PAGE_CUSTOMISE), "Toyota For You Customise Vehicle"],
  [getHyphenFromCamelCase(PAGE_FEATURES), "Toyota For You Vehicle Features"],
  [getHyphenFromCamelCase(PAGE_FINANCE), "Toyota For You Finance"],
  [getHyphenFromCamelCase(PAGE_VEHICLE_SELECTION), "Toyota For You Vehicle Selection"],
]);

const useGAPageView = () => {
  const location = useLocation();
  const dataContext = useDataContext();
  const { triggerGAEvent } = useGA();

  const getLastPartOfPath = (inputPath: string) => {
    const cleanedPath = inputPath.replaceAll(/^\/+|\/+$/g, "");
    const parts = cleanedPath.split("/");
    const lastPart = parts.at(-1);

    return `/${lastPart}`;
  };

  // Transforms a URL path into a human-readable format. For example, given "/home/share", this function returns "Home > Share".
  const getTransformedPath = (inputPath: string): string => {
    let cleanedPath = inputPath.replaceAll(/^\/+|\/+$/g, "");
    cleanedPath = cleanedPath.replaceAll(/\b\w/g, (match) => match.toUpperCase());
    cleanedPath = cleanedPath.replaceAll("/", ">");

    return cleanedPath;
  };

  const getPageTitle = useCallback(() => {
    const transformedPath = getTransformedPath(location?.pathname);
    const lastPartOfPath = getLastPartOfPath(location?.pathname);
    return `${transformedPath} - ${pageTitleMap.get(lastPartOfPath)}`;
  }, [location?.pathname]);

  const getPageReferrer = useCallback(() => {
    if (document?.referrer && document?.referrer !== "") {
      return document.referrer;
    }
    if (location?.state?.previousLocation) {
      const { pathname, search = "", state } = location.state.previousLocation;
      if (state) {
        const url = new URL(pathname, globalThis.window.location.origin);
        url.search = search;
        return url.toString();
      }
    }
    return "";
  }, [location?.state?.previousLocation]);

  useEffect(() => {
    const pageLocation = globalThis.window.location.href;
    const pageReferrer = getPageReferrer();
    const pageTitle = getPageTitle();

    if (dataContext?.dealershipData?.name) {
      triggerGAEvent(GaEvent.PAGE_VIEW, {
        pageLocation,
        pageReferrer,
        pageTitle,
        pmaName: dataContext?.dealershipData?.pmaName,
        sapCode: dataContext?.dealershipData?.sapCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, dataContext?.dealershipData, getPageTitle, getPageReferrer]);
};

export default useGAPageView;
